import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { leadership as leaders } from "../data/data";

// Page Header
const PageHeader = () => (
  <section className="bg-pc-primary pt-32 pb-20 texture-grain">
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-[2px] bg-pc-accent" />
        <span className="text-pc-accent font-heading text-sm font-semibold uppercase tracking-[0.15em]">
          Our Team
        </span>
      </div>
      <h1 className="font-heading text-3xl md:text-4xl lg:text-[52px] font-extrabold text-white leading-[1.1] tracking-tight mb-5">
        Leadership
      </h1>
      <p className="font-body text-white/50 text-lg max-w-2xl leading-relaxed">
        The people who lead Prayosha Construction bring decades of combined experience
        and a shared commitment to quality, honesty, and client satisfaction.
      </p>
    </motion.div>
  </section>
);

// Team Section
const TeamSection = () => {

  return (
    <section className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {leaders.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                className="border-pc-border/60 hover:shadow-xl card-accent-hover transition-shadow duration-300 rounded-xl h-full"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pc-primary/10 to-pc-accent/10 flex items-center justify-center">
                      <span className="font-heading text-xl font-extrabold text-pc-primary">
                        {member.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-xl font-bold text-pc-primary mb-1">
                        {member.name}
                      </h3>
                      <p className="font-heading text-pc-accent text-sm font-semibold mb-1">
                        {member.role}
                      </p>
                      <div className="flex items-center gap-1.5 text-pc-muted text-xs font-body mb-4">
                        <Clock className="w-3.5 h-3.5" />
                        {member.experience}
                      </div>
                      <Separator className="mb-4 bg-pc-border/50" />
                      <p className="font-body text-sm text-pc-muted leading-relaxed">
                        {member.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Company Culture
const CultureSection = () => (
  <section className="bg-pc-light py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl"
      >
        <div className="accent-bar mb-5" />
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-pc-primary tracking-tight mb-6">
          Built on Personal Accountability
        </h2>
        <div className="space-y-5 font-body text-pc-muted text-[15px] leading-[1.75]">
          <p>
            As a family-owned firm, accountability runs deep in our culture. Every
            project has a clear point of contact — someone who knows your project
            inside out and takes personal responsibility for its success.
          </p>
          <p>
            Our engineers and site supervisors are experienced professionals who have
            been with us for years. This stability means consistent quality and the
            kind of institutional knowledge that only comes from long tenures.
          </p>
          <p>
            We invest in our workers' skills and safety. Regular training, proper
            equipment, and fair working conditions are non-negotiable parts of how
            we operate.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

// CTA
const CtaSection = () => (
  <section className="bg-pc-primary py-20 texture-grain">
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-4">
        Ready to Build Something Together?
      </h2>
      <p className="font-body text-white/50 text-base max-w-lg mx-auto mb-8">
        Our team is ready to discuss your construction requirements.
      </p>
      <Link to="/contact">
        <Button className="bg-pc-accent hover:bg-pc-accent/90 text-white text-sm font-heading font-semibold px-8 h-12 rounded-lg transition-colors duration-200">
          Contact Our Team
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </motion.div>
  </section>
);

export default function Leadership() {
  return (
    <main>
      <PageHeader />
      <TeamSection />
      <CultureSection />
      <CtaSection />
    </main>
  );
}
